import { useEffect, useRef } from "react";
import { useCookies } from "react-cookie";

import { AUTH_COOKIE, getAuthorizationTokenExpiration } from "../services/auth/authToken";

const REFRESH_BUFFER_MS = 60_000;
const COOKIE_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;
const GOOGLE_PREFIX = "GO::";
const GOOGLE_CLIENT_ID = "509917624249-5ociqc44vp23feb7i78ifgq1g6aiupel.apps.googleusercontent.com";

type GoogleCredentialResponse = { credential?: string };

declare global {
	interface Window {
		google?: {
			accounts: {
				id: {
					initialize: (config: {
						client_id: string;
						callback: (response: GoogleCredentialResponse) => void;
						auto_select?: boolean;
						cancel_on_tap_outside?: boolean;
					}) => void;
					prompt: () => void;
				};
			};
		};
	}
}

export function useGoogleTokenRefresh(): void {
	const [cookies, setCookie] = useCookies([AUTH_COOKIE]);
	const token: string | undefined = cookies[AUTH_COOKIE];
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}

		if (!token?.startsWith(GOOGLE_PREFIX)) return;

		const expiresAt = getAuthorizationTokenExpiration(token);
		if (!expiresAt) return;

		const delay = Math.max(0, expiresAt.getTime() - REFRESH_BUFFER_MS - Date.now());

		timerRef.current = setTimeout(() => {
			if (!window.google?.accounts?.id) return;

			try {
				window.google.accounts.id.initialize({
					client_id: GOOGLE_CLIENT_ID,
					callback: (response) => {
						if (!response?.credential) return;
						setCookie(AUTH_COOKIE, `${GOOGLE_PREFIX}${response.credential}`, {
							path: "/",
							expires: new Date(Date.now() + COOKIE_EXPIRY_MS),
						});
					},
					auto_select: true,
					cancel_on_tap_outside: false,
				});
				window.google.accounts.id.prompt();
			} catch {
				// Silent refresh failed; the 401 response interceptor will handle expiry.
			}
		}, delay);

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		};
	}, [token, setCookie]);
}
